const CHUNK_BYTES = 4 * 1024 * 1024;
const FIRST_FRAME_MARGIN_SEC = 0.001;

/** A browser that lists audio tracks switches them itself; others play only the first one of an MP4. */
export function libraryNeedsMse(video: HTMLVideoElement): boolean {
  return !('audioTracks' in video) && typeof MediaSource !== 'undefined';
}

/** The next bytes to read, or null at the end of the file. */
export function nextRange(position: number, size: number, chunk = CHUNK_BYTES): { start: number; end: number } | null {
  if (position >= size) return null;
  return { start: position, end: Math.min(position + chunk, size) - 1 };
}

/** The end of the buffered range holding `time`, or `time` itself when none does. */
export function bufferedEnd(ranges: Pick<TimeRanges, 'length' | 'start' | 'end'>, time: number, slack = 0.25): number {
  for (let index = 0; index < ranges.length; index += 1) {
    if (time >= ranges.start(index) - slack && time < ranges.end(index)) return ranges.end(index);
  }
  return time;
}

/**
 * What Media Source still has to shift a track by, for its edit list. The browser
 * applies a first edit that starts in the media itself, but ignores a list that
 * opens with an empty edit, and then a track sits up to a few frames off the
 * others (measured on real episodes: 21-61 ms).
 */
export function presentationOffset(edits: Array<{ segment_duration: number; media_time: number }>, movieTimescale: number, trackTimescale: number): number {
  if (!edits.length || edits[0].media_time !== -1) return 0;
  let offset = 0;
  for (const edit of edits) {
    if (edit.media_time !== -1) return offset - edit.media_time / trackTimescale;
    offset += edit.segment_duration / movieTimescale;
  }
  return offset;
}

/**
 * The offset a video track may take: a frame pushed before 0 is dropped by the
 * browser with every frame up to the next keyframe, seconds of picture
 * (measured on a test file: 3.9 s).
 */
export function videoOffset(offset: number, firstPresentationSec: number): number {
  return offset < 0 ? Math.max(offset, FIRST_FRAME_MARGIN_SEC - firstPresentationSec) : offset;
}
